import { useEffect, useState } from 'react';
import { Percent } from 'lucide-react';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import ActionButton from '../../../components/Buttons/ActionButton';
import SelectField from '../../../components/Forms/Input/SelectField';
import InputField from '../../../components/Forms/Input/InputField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import { useBranch } from '../../../hooks/branches/useBranch';
import useAlerts from '../../../hooks/useAlerts';
import SaleService from '../../../services/sale/saleService';

export default function SalesTipSettingsPage() {
  const { fetchSalesBranchDropdown, branchDropdown, myBranch, fetchMyBranch } =
    useBranch();
  const { show } = useAlerts();

  const [branchId, setBranchId] = useState<number | null>(null);
  const [tipEnabled, setTipEnabled] = useState(false);
  const [tipPercentage, setTipPercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSalesBranchDropdown();
    fetchMyBranch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!branchId && myBranch?.id) {
      setBranchId(myBranch.id);
    }
  }, [myBranch, branchId]);

  useEffect(() => {
    if (!branchId) return;

    const load = async () => {
      try {
        setLoading(true);
        const response = await SaleService.getTipSettings(branchId);
        if (response?.statusCode === 200 && response.data) {
          setTipEnabled(Boolean(response.data.tipEnabled));
          setTipPercentage(Number(response.data.tipPercentage ?? 0));
        }
      } catch (error: any) {
        show(
          'error',
          'Error',
          error?.response?.data?.message ||
            'No se pudo cargar la configuración de propina.'
        );
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [branchId]);

  const save = async () => {
    if (!branchId) return;

    if (tipEnabled && (!tipPercentage || tipPercentage <= 0)) {
      show(
        'error',
        'Porcentaje inválido',
        'Debe indicar un porcentaje de propina mayor a cero para habilitarla.'
      );
      return;
    }

    try {
      setSaving(true);
      const response = await SaleService.updateTipSettings(branchId, {
        tipEnabled,
        tipPercentage,
      });

      if (response?.statusCode === 200) {
        show('success', 'Guardado', 'Configuración de propina actualizada.');
      } else {
        show('error', 'Error', 'No se pudo guardar la configuración.');
      }
    } catch (error: any) {
      show(
        'error',
        'Error',
        error?.response?.data?.message ||
          'No se pudo guardar la configuración de propina.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <main className="space-y-5 rounded-xl border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark sm:p-6">
        <header className="flex items-start gap-3">
          <div className="rounded-xl bg-primary/10 p-3 text-primary">
            <Percent className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-black dark:text-white">
              Propina
            </h1>
            <p className="mt-1 text-sm text-body">
              Permite cobrar una propina opcional en el módulo de Ventas. El
              cajero decide por cada venta si la cobra, desde un checkbox en
              el encabezado de la venta.
            </p>
          </div>
        </header>

        <section className="max-w-md space-y-5">
          <SelectField
            label="Sucursal"
            name="branch"
            value={branchId ?? ''}
            onChange={(event) => setBranchId(Number(event.target.value))}
            options={branchDropdown.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
            iconKey="branch"
          />

          <div className="rounded-xl border border-stroke bg-gray-2 px-5 py-5 dark:border-strokedark dark:bg-meta-4/20">
            <ToggleSwitch
              label="Habilitar propina"
              name="tip-enabled"
              checked={tipEnabled}
              disabled={loading}
              onChange={() => setTipEnabled(!tipEnabled)}
            />
            <p className="mt-2 text-xs leading-5 text-bodydark2 dark:text-bodydark">
              Mientras esté activa, la venta nace cobrando la propina por
              defecto; el cajero puede destildarla si el cliente no desea
              pagarla.
            </p>
          </div>

          <InputField
            label="Porcentaje de propina"
            name="tipPercentage"
            type="number"
            iconKey="percentage"
            min={0.01}
            max={100}
            step="0.01"
            disabled={!tipEnabled || loading}
            value={tipPercentage}
            onChange={(event) => setTipPercentage(Number(event.target.value))}
            rightContent="%"
          />

          <ActionButton
            label="Guardar"
            loading={saving}
            disabled={!branchId || loading}
            onClick={save}
          />
        </section>
      </main>
    </WorkspaceLayout>
  );
}
